"use client";
import Link from "next/link";
import Image from "next/image";
import { FaArrowRight } from "react-icons/fa";
import { servicesData } from "@/data/service.data";
import CtaSection from "../shared/CtaSection";
import { useDict } from "@/components/providers/LocaleProvider";
import { TransitionLink } from "@/components/shared/TransitionLink";

export default function ServiceFlow() {
  const t = useDict();
  return (
    <div className="min-h-screen bg-background ">
      {/* Hero Section */}
      <section className="relative  py-14 flex pt-32 items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="space-y-8 animate-fade-in-up">
            <h2 className="font-black section-title text-5xl md:text-7xl lg:text-8xl text-foreground leading-tight">
              {t.servicesPage.hero.titleLine1}
              <span className="block text-primary">
                {t.servicesPage.hero.titleLine2}
              </span>
            </h2>
            <p className="section-description text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {t.servicesPage.hero.description}
            </p>

            <Link
              href="#services"
              className="inline-flex items-center justify-center  font-medium text-lg px-8 py-2 rounded-full group hover:scale-105 transition-all duration-300 bg-primary text-primary-foreground"
            >
              {t.servicesPage.hero.exploreCta}
              <FaArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full animate-float" />
        <div className="absolute bottom-32 right-16 w-16 h-16 bg-accent/15 rounded-full animate-float-delayed" />
        <div className="absolute top-1/2 right-8 w-12 h-12 bg-primary/5 rounded-full animate-float" />
      </section>

      <div id="services" className="relative">
        <div className="absolute left-1/2 transform -translate-x-0.5 w-px bg-gradient-to-b from-primary/20 via-primary/40 to-primary/20 h-full hidden lg:block" />

        {servicesData.map((service, index) => {
          const right = index % 2 !== 0;
          const copy = t.servicesPage.services[service.key];
          return (
            <section
              key={service.id}
              className="relative py-20 lg:py-32 opacity-100 translate-y-0 animate-fade-in-up"
            >
              <div className="max-w-7xl mx-auto px-6">
                <div
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                    right ? "lg:grid-flow-col-dense" : ""
                  }`}
                >
                  <div className={`space-y-8 ${right ? "lg:col-start-2" : ""}`}>
                    <div className="space-y-4">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                        <span className=" font-medium text-primary text-sm uppercase tracking-wider">
                          {copy.title}
                        </span>
                      </div>

                      <h2 className="font-black text-3xl md:text-5xl lg:text-6xl text-foreground leading-tight">
                        {copy.title}
                      </h2>

                      <p className=" text-lg md:text-xl text-muted-foreground leading-relaxed max-w-lg">
                        {copy.description}
                      </p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {service.subServices.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center gap-3 p-4 bg-card rounded-xl border  border-light-border cursor-pointer hover:border-primary/20 transition-colors group"
                        >
                          <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                            <feature.Icon className=" text-white" />
                          </div>
                          <span className=" font-medium text-foreground">
                            {copy.subServices[featureIndex] ?? ""}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-4">
                      <TransitionLink
                        href={`/book-a-meeting`}
                        className="inline-flex items-center justify-center  font-semibold px-6 py-3 rounded-lg border border-primary/20 bg-primary hover:text-primary-foreground transition-all duration-300 hover:bg-primary/90"
                      >
                        {t.servicesPage.letsTalk}
                        <FaArrowRight className="ml-2 h-4 w-4" />
                      </TransitionLink>
                    </div>
                  </div>

                  <div className={`relative ${right ? "lg:col-start-1" : ""}`}>
                    <div className="relative aspect-video rounded-3xl overflow-hidden">
                      <Image
                        src={service.background as string}
                        alt={copy.title}
                        fill
                        sizes="(min-width: 1024px) 50vw, 100vw"
                        className="object-cover"
                        priority={index < 2}
                      />

                      <div className="absolute top-8 right-8 w-16 h-16 bg-white/10 rounded-full animate-float" />
                      <div className="absolute bottom-12 left-12 w-12 h-12 bg-white/5 rounded-full animate-float-delayed" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full border-4 border-background shadow-lg hidden lg:block" />
            </section>
          );
        })}
      </div>

      <CtaSection />
    </div>
  );
}
